export type FerramentaHidraulicaAnimada = 'alargador' | 'cortadorHidraulico' | 'extensor'

export interface PoseFerramentaHidraulica {
  progresso: number
  anguloMandibula: number
  cursoExtensor: number
  inclinacaoAlavancaRad: number
}

/** Abertura das garras em cada estado inicial, em radianos de `anguloMandibula`. */
const ANGULO_ESTADO_INICIAL: Record<'alargador' | 'cortadorHidraulico', Record<'aberto' | 'fechado', number>> = {
  alargador: { fechado: 0, aberto: 0.58 },
  cortadorHidraulico: { aberto: 0.72, fechado: 0.08 },
}

/**
 * Mistura a abertura do estado inicial com a da pose de trabalho. `preparo`
 * vai de 0 (garras no estado inicial, ferramenta no ar) a 1 (pose de contato
 * e execução): é assim que o alargador chega fechado e abre no ar antes de
 * encaixar, e que o cortador reabre ao soltar a peça.
 */
export function anguloComPreparo(
  ferramenta: FerramentaHidraulicaAnimada,
  anguloPose: number,
  preparo: number,
  estadoInicial: 'aberto' | 'fechado' | null | undefined,
): number {
  if (ferramenta === 'extensor' || !estadoInicial) return anguloPose
  const k = limitar(preparo, 0, 1)
  const inicial = ANGULO_ESTADO_INICIAL[ferramenta][estadoInicial]
  return inicial + (anguloPose - inicial) * k
}

function limitar(valor: number, minimo: number, maximo: number): number {
  return Math.min(maximo, Math.max(minimo, valor))
}

/**
 * Converte o progresso da operação em uma pose reproduzível. A cena recebe o
 * progresso do domínio e não decide, por conta própria, se houve corte ou
 * deformação no veículo.
 */
export function poseFerramentaHidraulica(
  ferramenta: FerramentaHidraulicaAnimada,
  progresso: number,
  modoAlargador: 'abrir' | 'fechar' = 'abrir',
): PoseFerramentaHidraulica {
  const normalizado = limitar(progresso, 0, 1)
  if (ferramenta === 'extensor') {
    return {
      progresso: normalizado,
      anguloMandibula: 0,
      cursoExtensor: 0.18 + normalizado * 1.12,
      inclinacaoAlavancaRad: 0,
    }
  }
  if (ferramenta === 'alargador') {
    const anguloFechar = normalizado <= 0.5
      ? 0.52 - (normalizado / 0.5) * (0.52 - 0.05)
      : 0.05 + ((normalizado - 0.5) / 0.5) * (0.34 - 0.05)
    // No modo fechar (pinçamento do para-lama), as pontas esmagam e ao reabrir
    // a ferramenta inclina para a frente, alavancando e criando o vão de abertura
    const inclinacaoAlavanca = modoAlargador === 'fechar' && normalizado > 0.5
      ? ((normalizado - 0.5) / 0.5) * 0.14
      : 0
    return {
      progresso: normalizado,
      anguloMandibula: modoAlargador === 'abrir'
        ? normalizado * 0.58
        : anguloFechar,
      cursoExtensor: 0,
      inclinacaoAlavancaRad: inclinacaoAlavanca,
    }
  }
  return {
    progresso: normalizado,
    anguloMandibula: 0.72 - normalizado * 0.64,
    cursoExtensor: 0,
    inclinacaoAlavancaRad: 0,
  }
}
